import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Upload, Download, Eye, Trash2, Filter, TrendingUp,
    BarChart3, Pencil, Save, X, Plus, Calendar, FileText,
    Loader2, Sparkles, GitCompare, ChevronRight, Search,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { CATEGORY_META, FLAG_META, fmtExamDate, humanFileSize } from "@/lib/exams";
import ExamUploadDialog from "./ExamUploadDialog";
import ExamPreviewModal from "./ExamPreviewModal";
import LabTrendChart from "./LabTrendChart";

const FLAG_OPTIONS = [
    { value: "", label: "—" },
    { value: "low", label: "Baixo" },
    { value: "normal", label: "Normal" },
    { value: "high", label: "Alto" },
    { value: "critical", label: "Crítico" },
];

export default function ExamsTimeline({
    patientId,
    consultationId = null,
    canUpload = true,
    canEdit = true,
    embedded = false, // when true, hides the outer heading
}) {
    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showUpload, setShowUpload] = useState(false);
    const [preview, setPreview] = useState(null);
    const [editing, setEditing] = useState(null); // exam id
    const [editRows, setEditRows] = useState([]);
    const [category, setCategory] = useState("");
    const [search, setSearch] = useState("");
    const [trendAnalyte, setTrendAnalyte] = useState(null);
    const [compareIds, setCompareIds] = useState([]); // up to 2

    const refresh = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/patients/${patientId}/exams`);
            setExams(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [patientId]);

    const filtered = useMemo(() => {
        return exams.filter((e) => {
            if (category && e.category !== category) return false;
            if (search) {
                const s = search.toLowerCase();
                const inLabel = (e.exam_type_label || "").toLowerCase().includes(s);
                const inNotes = (e.notes || "").toLowerCase().includes(s);
                const inResults = (e.results || []).some((r) => (r.analyte || "").toLowerCase().includes(s));
                if (!inLabel && !inNotes && !inResults) return false;
            }
            return true;
        });
    }, [exams, category, search]);

    // Group by month for the timeline
    const groups = useMemo(() => {
        const byMonth = new Map();
        for (const e of filtered) {
            const d = e.exam_date || e.created_at;
            const k = d ? d.slice(0, 7) : "sem-data";
            if (!byMonth.has(k)) byMonth.set(k, []);
            byMonth.get(k).push(e);
        }
        return Array.from(byMonth.entries())
            .sort((a, b) => b[0].localeCompare(a[0]))
            .map(([month, items]) => ({ month, items }));
    }, [filtered]);

    // Distinct analytes across all lab exams — used for the trend chip cloud
    const analytes = useMemo(() => {
        const set = new Map();
        for (const e of exams) {
            if (e.category !== "laboratorial") continue;
            for (const r of e.results || []) {
                if (!r.analyte) continue;
                const key = r.analyte.trim();
                const stat = set.get(key) || { count: 0 };
                stat.count += 1;
                set.set(key, stat);
            }
        }
        return Array.from(set.entries())
            .filter(([, s]) => s.count >= 2) // only ones with at least 2 data points
            .sort((a, b) => b[1].count - a[1].count)
            .map(([name, s]) => ({ name, count: s.count }));
    }, [exams]);

    // Analyte history: for each analyte, ordered list of {date, value, examId}.
    // Used to render inline delta ("↑ 0,3 desde 12/03") next to each result.
    const analyteHistory = useMemo(() => {
        const map = new Map(); // analyteKey -> [{date, value, unit, examId}]
        const labs = [...exams]
            .filter((e) => e.category === "laboratorial" && e.exam_date)
            .sort((a, b) => (a.exam_date || "").localeCompare(b.exam_date || ""));
        for (const e of labs) {
            for (const r of e.results || []) {
                if (!r.analyte) continue;
                const key = r.analyte.trim().toLowerCase();
                if (!map.has(key)) map.set(key, []);
                map.get(key).push({
                    date: e.exam_date,
                    value: r.value,
                    unit: r.unit,
                    examId: e.id,
                });
            }
        }
        return map;
    }, [exams]);

    const startEdit = (exam) => {
        setEditing(exam.id);
        setEditRows((exam.results || []).map((r) => ({ ...r })));
    };
    const cancelEdit = () => { setEditing(null); setEditRows([]); };
    const saveEdit = async (examId) => {
        try {
            const { data } = await api.put(`/exams/${examId}`, { results: editRows.filter((r) => r.analyte) });
            setExams((prev) => prev.map((e) => (e.id === examId ? data : e)));
            toast.success("Resultados atualizados.");
            cancelEdit();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const deleteExam = async (exam) => {
        if (!window.confirm(`Excluir o exame "${exam.exam_type_label}"?`)) return;
        try {
            await api.delete(`/exams/${exam.id}`);
            setExams((prev) => prev.filter((e) => e.id !== exam.id));
            toast.success("Exame excluído.");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const toggleCompare = (id) => {
        setCompareIds((prev) => {
            if (prev.includes(id)) return prev.filter((x) => x !== id);
            if (prev.length >= 2) return [prev[1], id];
            return [...prev, id];
        });
    };

    const compareData = useMemo(() => {
        if (compareIds.length !== 2) return null;
        const [a, b] = compareIds.map((id) => exams.find((e) => e.id === id)).filter(Boolean);
        if (!a || !b) return null;
        // Build unified analyte set
        const map = new Map();
        for (const r of a.results || []) map.set(r.analyte, { analyte: r.analyte, a: r, b: null });
        for (const r of b.results || []) {
            const existing = map.get(r.analyte);
            if (existing) existing.b = r;
            else map.set(r.analyte, { analyte: r.analyte, a: null, b: r });
        }
        return { a, b, rows: Array.from(map.values()) };
    }, [compareIds, exams]);

    return (
        <div className="space-y-4" data-testid="exams-timeline">
            {!embedded && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <h2 className="font-display text-2xl text-brand">Exames</h2>
                        <p className="text-xs text-muted-clinical">
                            Todos os exames deste paciente em ordem cronológica. Anexe laudos em PDF ou imagens.
                        </p>
                    </div>
                    {canUpload && (
                        <button onClick={() => setShowUpload(true)} className="btn-primary" data-testid="exams-upload-btn">
                            <Upload size={14} /> Anexar exame
                        </button>
                    )}
                </div>
            )}

            {embedded && canUpload && (
                <div className="flex justify-end">
                    <button onClick={() => setShowUpload(true)} className="btn-secondary text-sm" data-testid="exams-upload-btn-embedded">
                        <Upload size={14} /> Anexar exame nesta consulta
                    </button>
                </div>
            )}

            {/* Filters + analyte trends */}
            <div className="card-earthy space-y-3">
                <div className="flex flex-wrap items-end gap-2">
                    <div className="flex-1 min-w-[180px]">
                        <label className="label-clinical text-xs">Buscar</label>
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-clinical" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Tipo de exame, analito, observação..."
                                className="input-clinical pl-9"
                                data-testid="exams-search-input"
                            />
                        </div>
                    </div>
                    <div className="min-w-[180px]">
                        <label className="label-clinical text-xs">Categoria</label>
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="input-clinical"
                            data-testid="exams-filter-category"
                        >
                            <option value="">Todas</option>
                            {Object.entries(CATEGORY_META).map(([k, v]) => (
                                <option key={k} value={k}>{v.label}</option>
                            ))}
                        </select>
                    </div>
                    {compareIds.length > 0 && (
                        <button
                            onClick={() => setCompareIds([])}
                            className="btn-ghost text-xs"
                            data-testid="exams-compare-reset"
                        >
                            Limpar comparação ({compareIds.length}/2)
                        </button>
                    )}
                </div>

                {analytes.length > 0 && (
                    <div className="pt-2 border-t border-[var(--border-organic)]">
                        <p className="text-[11px] uppercase tracking-widest text-brand-secondary font-medium mb-2 flex items-center gap-1">
                            <TrendingUp size={12} /> Tendências disponíveis
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                            {analytes.slice(0, 20).map((a) => (
                                <button
                                    key={a.name}
                                    onClick={() => setTrendAnalyte(a.name)}
                                    className="px-2 py-1 rounded-full text-xs bg-alt text-brand hover:bg-[var(--brand-secondary)] hover:text-white transition-colors"
                                    data-testid={`analyte-chip-${a.name.replace(/\s+/g, '-').toLowerCase()}`}
                                    title={`${a.count} medições ao longo do tempo`}
                                >
                                    {a.name} · {a.count}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {trendAnalyte && (
                <LabTrendChart
                    patientId={patientId}
                    analyte={trendAnalyte}
                    onClose={() => setTrendAnalyte(null)}
                />
            )}

            {compareData && (
                <CompareBlock data={compareData} onClose={() => setCompareIds([])} />
            )}

            {/* Timeline */}
            {loading ? (
                <div className="card-earthy flex items-center gap-2 text-sm text-muted-clinical">
                    <Loader2 className="animate-spin" size={14} /> Carregando exames...
                </div>
            ) : groups.length === 0 ? (
                <div className="card-earthy text-sm text-muted-clinical text-center py-10">
                    <FileText size={32} className="mx-auto text-brand/40 mb-2" />
                    <p>Nenhum exame anexado ainda.</p>
                    {canUpload && (
                        <button onClick={() => setShowUpload(true)} className="btn-primary text-sm mt-3">
                            <Upload size={14} /> Anexar primeiro exame
                        </button>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    {groups.map((g) => (
                        <div key={g.month}>
                            <div className="flex items-center gap-2 mb-2">
                                <div className="h-px flex-1 bg-[var(--border-organic)]"></div>
                                <span className="text-xs uppercase tracking-widest text-brand-secondary font-medium">
                                    {monthLabel(g.month)}
                                </span>
                                <div className="h-px flex-1 bg-[var(--border-organic)]"></div>
                            </div>
                            <div className="space-y-2">
                                {g.items.map((exam) => (
                                    <ExamCard
                                        key={exam.id}
                                        exam={exam}
                                        analyteHistory={analyteHistory}
                                        onPreview={() => setPreview(exam)}
                                        onEdit={() => startEdit(exam)}
                                        onDelete={() => deleteExam(exam)}
                                        onToggleCompare={() => toggleCompare(exam.id)}
                                        onAnalyteClick={(a) => setTrendAnalyte(a)}
                                        isEditing={editing === exam.id}
                                        editRows={editRows}
                                        setEditRows={setEditRows}
                                        saveEdit={() => saveEdit(exam.id)}
                                        cancelEdit={cancelEdit}
                                        canEdit={canEdit}
                                        compareChecked={compareIds.includes(exam.id)}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showUpload && (
                <ExamUploadDialog
                    patientId={patientId}
                    consultationId={consultationId}
                    onClose={() => setShowUpload(false)}
                    onSaved={() => refresh()}
                />
            )}
            {preview && (
                <ExamPreviewModal exam={preview} onClose={() => setPreview(null)} />
            )}
        </div>
    );
}

function monthLabel(ym) {
    if (ym === "sem-data") return "Sem data";
    const [y, m] = ym.split("-");
    const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return d.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
}

function ExamCard({
    exam, analyteHistory, onPreview, onEdit, onDelete, onToggleCompare, onAnalyteClick,
    isEditing, editRows, setEditRows, saveEdit, cancelEdit, canEdit, compareChecked,
}) {
    const meta = CATEGORY_META[exam.category] || CATEGORY_META.outros;
    const flagCounts = {
        high: 0, low: 0, critical: 0, normal: 0,
    };
    for (const r of exam.results || []) {
        if (r.flag && flagCounts[r.flag] != null) flagCounts[r.flag]++;
    }
    const abnormal = flagCounts.high + flagCounts.low + flagCounts.critical;

    // For each analyte in this exam, find the most recent PREVIOUS measurement
    // (strictly earlier date) so we can render "↑ 0,3 desde 12/03" inline.
    const previousLookup = (analyteName) => {
        if (!analyteHistory) return null;
        const key = (analyteName || "").trim().toLowerCase();
        const history = analyteHistory.get(key);
        if (!history || history.length < 2) return null;
        // Locate current exam entry, then walk back for the previous exam
        const idx = history.findIndex((h) => h.examId === exam.id);
        if (idx <= 0) return null;
        // Walk backwards to find first strictly earlier date
        const currentDate = history[idx].date;
        for (let i = idx - 1; i >= 0; i--) {
            if (history[i].date && history[i].date < currentDate) return history[i];
        }
        return null;
    };

    return (
        <div
            className="card-earthy border-l-4"
            style={{ borderLeftColor: meta.color }}
            data-testid={`exam-card-${exam.id}`}
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span
                            className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full"
                            style={{ background: meta.accent + "60", color: meta.color }}
                        >
                            {meta.label}
                        </span>
                        <h3 className="font-medium text-brand">{exam.exam_type_label || exam.exam_type}</h3>
                        {exam.extracted_by_ai && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-[#EDE4FF] text-[#5B3F72] rounded-full font-semibold flex items-center gap-1">
                                <Sparkles size={9} /> IA
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-muted-clinical mt-1 flex items-center gap-2 flex-wrap">
                        <Calendar size={11} /> {fmtExamDate(exam.exam_date)}
                        {exam.original_filename && (<>· <FileText size={11} /> {exam.original_filename} ({humanFileSize(exam.size)})</>)}
                        {abnormal > 0 && (
                            <span className="text-[var(--brand-accent)] font-medium">
                                · {abnormal} valor{abnormal > 1 ? "es" : ""} fora da referência
                            </span>
                        )}
                    </p>
                    {exam.notes && !isEditing && (
                        <p className="text-sm text-brand-secondary mt-2 whitespace-pre-wrap">{exam.notes}</p>
                    )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    {canEdit && (
                        <label className="text-xs flex items-center gap-1 mr-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={compareChecked}
                                onChange={onToggleCompare}
                                className="w-3.5 h-3.5"
                                data-testid={`exam-compare-toggle-${exam.id}`}
                            />
                            <GitCompare size={12} />
                        </label>
                    )}
                    <button onClick={onPreview} className="btn-ghost text-xs" title="Visualizar" data-testid={`exam-preview-${exam.id}`}>
                        <Eye size={14} /> Ver
                    </button>
                    {canEdit && !isEditing && exam.category === "laboratorial" && (
                        <button onClick={onEdit} className="btn-ghost text-xs" title="Editar resultados" data-testid={`exam-edit-${exam.id}`}>
                            <Pencil size={14} />
                        </button>
                    )}
                    {canEdit && (
                        <button onClick={onDelete} className="btn-ghost text-xs text-red-700 hover:bg-red-50" title="Excluir" data-testid={`exam-delete-${exam.id}`}>
                            <Trash2 size={14} />
                        </button>
                    )}
                </div>
            </div>

            {isEditing ? (
                <EditResults
                    rows={editRows}
                    setRows={setEditRows}
                    onSave={saveEdit}
                    onCancel={cancelEdit}
                />
            ) : (exam.results && exam.results.length > 0) && (
                <div className="mt-3 pt-3 border-t border-[var(--border-organic)]">
                    <div className="grid md:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                        {exam.results.map((r, i) => {
                            const flagMeta = r.flag ? FLAG_META[r.flag] : null;
                            const prev = previousLookup(r.analyte);
                            const curN = numeric(r.value);
                            const prevN = prev ? numeric(prev.value) : null;
                            let delta = null;
                            if (curN != null && prevN != null) {
                                const diff = curN - prevN;
                                const sign = diff > 0 ? "↑" : diff < 0 ? "↓" : "→";
                                const cls = diff > 0
                                    ? "text-red-700"
                                    : diff < 0
                                        ? "text-green-700"
                                        : "text-muted-clinical";
                                // Preserva 2 casas para deltas pequenos, 1 para deltas maiores
                                const absDiff = Math.abs(diff);
                                const formatted = absDiff >= 10
                                    ? absDiff.toFixed(0)
                                    : absDiff >= 1
                                        ? absDiff.toFixed(1)
                                        : absDiff.toFixed(2);
                                delta = { sign, cls, formatted, prevDate: fmtExamDate(prev.date), prevValue: prev.value };
                            }
                            return (
                                <div key={i} className="py-0.5" data-testid={`exam-result-item-${i}`}>
                                    <button
                                        onClick={() => onAnalyteClick(r.analyte)}
                                        className="w-full flex items-center justify-between hover:bg-alt/40 rounded px-1 text-left"
                                        title="Ver tendência ao longo do tempo"
                                    >
                                        <span className="text-brand-secondary">{r.analyte}</span>
                                        <span className="flex items-center gap-1.5">
                                            <span className={`font-medium ${flagMeta && flagMeta.color !== "#1E3F33" ? "text-[var(--brand-accent)]" : "text-brand"}`}>
                                                {r.value}
                                            </span>
                                            {r.unit && <span className="text-xs text-muted-clinical">{r.unit}</span>}
                                            {flagMeta && flagMeta.label !== "Normal" && (
                                                <span
                                                    className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded"
                                                    style={{ background: flagMeta.bg, color: flagMeta.color }}
                                                >
                                                    {flagMeta.label}
                                                </span>
                                            )}
                                        </span>
                                    </button>
                                    {delta && (
                                        <div
                                            className={`text-[11px] pl-1 mt-0.5 flex items-center gap-1 ${delta.cls}`}
                                            data-testid={`exam-result-delta-${i}`}
                                            title={`Valor anterior: ${delta.prevValue} em ${delta.prevDate}`}
                                        >
                                            <span className="font-semibold">{delta.sign} {delta.formatted}</span>
                                            <span className="text-muted-clinical">desde {delta.prevDate}</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

function EditResults({ rows, setRows, onSave, onCancel }) {
    const update = (i, k, v) => setRows((r) => r.map((x, ix) => (ix === i ? { ...x, [k]: v } : x)));
    const remove = (i) => setRows((r) => r.filter((_, ix) => ix !== i));
    const add = () => setRows((r) => [...r, { analyte: "", value: "", unit: "", reference_range: "", flag: "" }]);
    return (
        <div className="mt-3 pt-3 border-t border-[var(--border-organic)] space-y-2">
            <div className="grid grid-cols-12 gap-2 text-[11px] uppercase tracking-wide text-brand-secondary font-medium">
                <div className="col-span-4">Analito</div>
                <div className="col-span-2">Valor</div>
                <div className="col-span-2">Unidade</div>
                <div className="col-span-2">Referência</div>
                <div className="col-span-1">Flag</div>
                <div className="col-span-1"></div>
            </div>
            {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input className="input-clinical col-span-4 py-1 text-sm" placeholder="Hemoglobina" value={r.analyte || ""} onChange={(e) => update(i, "analyte", e.target.value)} />
                    <input className="input-clinical col-span-2 py-1 text-sm" placeholder="13.5" value={r.value || ""} onChange={(e) => update(i, "value", e.target.value)} />
                    <input className="input-clinical col-span-2 py-1 text-sm" placeholder="g/dL" value={r.unit || ""} onChange={(e) => update(i, "unit", e.target.value)} />
                    <input className="input-clinical col-span-2 py-1 text-sm" placeholder="12-16" value={r.reference_range || ""} onChange={(e) => update(i, "reference_range", e.target.value)} />
                    <select className="input-clinical col-span-1 py-1 text-sm" value={r.flag || ""} onChange={(e) => update(i, "flag", e.target.value)}>
                        {FLAG_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <button onClick={() => remove(i)} className="text-muted-clinical hover:text-red-600 col-span-1"><Trash2 size={14} /></button>
                </div>
            ))}
            <div className="flex justify-between mt-2">
                <button onClick={add} className="btn-ghost text-xs"><Plus size={12} /> Adicionar linha</button>
                <div className="flex gap-2">
                    <button onClick={onCancel} className="btn-ghost text-xs">Cancelar</button>
                    <button onClick={onSave} className="btn-primary text-xs"><Save size={12} /> Salvar</button>
                </div>
            </div>
        </div>
    );
}

function CompareBlock({ data, onClose }) {
    const { a, b, rows } = data;
    return (
        <div className="card-earthy border-2 border-brand" data-testid="exam-compare-block">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="font-display text-lg text-brand flex items-center gap-2">
                    <GitCompare size={18} /> Comparativo
                </h3>
                <button onClick={onClose} className="btn-ghost text-xs">Fechar</button>
            </div>
            <div className="grid md:grid-cols-2 gap-3 mb-3 text-sm">
                <div className="card-earthy bg-alt/40 p-3">
                    <p className="text-xs text-muted-clinical">Exame A</p>
                    <p className="font-medium text-brand">{a.exam_type_label}</p>
                    <p className="text-xs text-muted-clinical">{fmtExamDate(a.exam_date)}</p>
                </div>
                <div className="card-earthy bg-alt/40 p-3">
                    <p className="text-xs text-muted-clinical">Exame B</p>
                    <p className="font-medium text-brand">{b.exam_type_label}</p>
                    <p className="text-xs text-muted-clinical">{fmtExamDate(b.exam_date)}</p>
                </div>
            </div>
            {rows.length === 0 ? (
                <p className="text-sm text-muted-clinical italic">Nenhum resultado estruturado para comparar.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[11px] uppercase tracking-wide text-brand-secondary border-b border-[var(--border-organic)]">
                                <th className="py-1.5 pr-2">Analito</th>
                                <th className="py-1.5 px-2">A · {fmtExamDate(a.exam_date)}</th>
                                <th className="py-1.5 px-2">B · {fmtExamDate(b.exam_date)}</th>
                                <th className="py-1.5 pl-2">Variação</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, i) => {
                                const nA = numeric(row.a?.value);
                                const nB = numeric(row.b?.value);
                                let delta = null;
                                if (nA != null && nB != null && nA !== 0) {
                                    const d = ((nB - nA) / nA) * 100;
                                    delta = `${d > 0 ? "+" : ""}${d.toFixed(1)}%`;
                                }
                                return (
                                    <tr key={i} className="border-b border-[var(--border-organic)]/50">
                                        <td className="py-1.5 pr-2 font-medium text-brand">{row.analyte}</td>
                                        <td className="py-1.5 px-2">{row.a ? `${row.a.value} ${row.a.unit || ""}` : "—"}</td>
                                        <td className="py-1.5 px-2">{row.b ? `${row.b.value} ${row.b.unit || ""}` : "—"}</td>
                                        <td className={`py-1.5 pl-2 font-medium ${delta && delta.startsWith("+") ? "text-red-700" : delta ? "text-green-700" : "text-muted-clinical"}`}>
                                            {delta || "—"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function numeric(v) {
    if (v == null) return null;
    const m = String(v).match(/-?\d+([.,]\d+)?/);
    return m ? parseFloat(m[0].replace(",", ".")) : null;
}
